import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Copy, Download, Upload, Settings, Code, FileText, Zap, Check, AlertCircle, 
  Moon, Sun, RotateCcw, Eye, EyeOff, Hash, Type, 
  ChevronDown, X, Square, Braces, HelpCircle, Maximize, Minimize,
  Palette, Filter, Layout, Terminal, Globe, Database
} from 'lucide-react';

// Type definitions
interface FormattingOptions {
  indentSize: number;
  indentType: 'spaces' | 'tabs';
  semicolons: boolean;
  quotes: 'single' | 'double';
  trailingCommas: 'none' | 'es5' | 'all';
  bracketSpacing: boolean;
  arrowParens: 'avoid' | 'always';
  maxLineLength: number;
  insertFinalNewline: boolean;
  trimTrailingWhitespace: boolean;
  preserveComments: boolean;
  sortImports: boolean;
  removeConsole: boolean;
  convertToArrowFunctions: boolean;
  addTypeAnnotations: boolean;
  useStrictMode: boolean;
}

type Language = 'javascript' | 'typescript' | 'json' | 'html' | 'css' | 'python' | 'php' | 'dart' | 'java' | 'cpp' | 'csharp' | 'go' | 'rust' | 'ruby' | 'swift' | 'kotlin' | 'xml' | 'yaml' | 'sql' | 'markdown';

const JavaScriptFormatterPro = () => {
  const [inputCode, setInputCode] = useState<string>('');
  const [outputCode, setOutputCode] = useState<string>('');
  const [language, setLanguage] = useState<Language>('javascript');
  const [darkMode, setDarkMode] = useState<boolean>(true);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [autoFormat, setAutoFormat] = useState<boolean>(true);
  const [showStats, setShowStats] = useState<boolean>(true);
  const [fontSize, setFontSize] = useState<number>(14);
  const [wordWrap, setWordWrap] = useState<boolean>(true);
  const [showLineNumbers, setShowLineNumbers] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showSettingsPanel, setShowSettingsPanel] = useState<boolean>(false);
  const [isFormatting, setIsFormatting] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  
  // Enhanced formatting options
  const [indentSize, setIndentSize] = useState<number>(2);
  const [indentType, setIndentType] = useState<'spaces' | 'tabs'>('spaces');
  const [semicolons, setSemicolons] = useState<boolean>(true);
  const [quotes, setQuotes] = useState<'single' | 'double'>('single');
  const [trailingCommas, setTrailingCommas] = useState<'none' | 'es5' | 'all'>('es5');
  const [bracketSpacing, setBracketSpacing] = useState<boolean>(true);
  const [arrowParens, setArrowParens] = useState<'avoid' | 'always'>('avoid');
  const [maxLineLength, setMaxLineLength] = useState<number>(80);
  const [insertFinalNewline, setInsertFinalNewline] = useState<boolean>(true);
  const [trimTrailingWhitespace, setTrimTrailingWhitespace] = useState<boolean>(true);
  const [preserveComments, setPreserveComments] = useState<boolean>(true);
  const [sortImports, setSortImports] = useState<boolean>(false);
  const [removeConsole, setRemoveConsole] = useState<boolean>(false);
  const [convertToArrowFunctions, setConvertToArrowFunctions] = useState<boolean>(false);
  const [addTypeAnnotations, setAddTypeAnnotations] = useState<boolean>(false);
  const [useStrictMode, setUseStrictMode] = useState<boolean>(false);
  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const outputRef = useRef<HTMLPreElement>(null);

  const languageOptions = [
    { value: 'javascript', label: 'JavaScript', icon: '🟨' },
    { value: 'typescript', label: 'TypeScript', icon: '🔷' },
    { value: 'json', label: 'JSON', icon: '📋' },
    { value: 'html', label: 'HTML', icon: '🌐' },
    { value: 'css', label: 'CSS', icon: '🎨' },
    { value: 'python', label: 'Python', icon: '🐍' },
    { value: 'php', label: 'PHP', icon: '🐘' },
    { value: 'dart', label: 'Dart', icon: '🎯' },
    { value: 'java', label: 'Java', icon: '☕' },
    { value: 'cpp', label: 'C++', icon: '⚡' },
    { value: 'csharp', label: 'C#', icon: '🔵' },
    { value: 'go', label: 'Go', icon: '🔷' },
    { value: 'rust', label: 'Rust', icon: '🦀' },
    { value: 'ruby', label: 'Ruby', icon: '💎' },
    { value: 'swift', label: 'Swift', icon: '🍎' },
    { value: 'kotlin', label: 'Kotlin', icon: '🟣' },
    { value: 'xml', label: 'XML', icon: '📄' },
    { value: 'yaml', label: 'YAML', icon: '📝' },
    { value: 'sql', label: 'SQL', icon: '🗃️' },
    { value: 'markdown', label: 'Markdown', icon: '📖' }
  ];

  const sampleCode: Record<Language, string> = {
    javascript: `function calculateTotal(items,tax=0.1){
const subtotal=items.reduce((sum,item)=>sum+item.price*item.quantity,0);
const taxAmount=subtotal*tax;
return{subtotal:subtotal,tax:taxAmount,total:subtotal+taxAmount,
items:items.map(item=>({...item,total:item.price*item.quantity}))};
}

const cart=[{name:"Laptop",price:999.99,quantity:1,category:"Electronics"},{name:"Mouse",price:25.50,quantity:2,category:"Accessories"}];

const result=calculateTotal(cart,0.08);
console.log("Order Summary:",result);

if(result.total>1000){console.log("Free shipping applied!");}else{console.log("Shipping: $9.99");}`,

    typescript: `interface Product {
name: string;price: number;quantity: number;category: string;
}
interface OrderSummary {
subtotal: number;tax: number;total: number;
items: Array<Product & { total: number }>;
}
function calculateTotal(items: Product[], tax: number = 0.1): OrderSummary {
const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
const taxAmount = subtotal * tax;
return {
subtotal: subtotal,tax: taxAmount,total: subtotal + taxAmount,
items: items.map(item => ({ ...item, total: item.price * item.quantity }))
};
}`,

    json: `{"users":[{"id":1,"name":"John Doe","email":"john@example.com","profile":{"age":30,"location":"New York","preferences":{"theme":"dark","notifications":true,"language":"en"}},"orders":[{"id":101,"date":"2024-01-15","items":[{"product":"Laptop","price":999.99,"quantity":1},{"product":"Mouse","price":25.50,"quantity":2}],"total":1050.99}]}],"meta":{"total_users":2,"created_at":"2024-01-20T10:30:00Z","version":"1.0"}}`,

    html: `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Sample Page</title></head><body><header><h1>Welcome to My Website</h1><nav><ul><li><a href="#home">Home</a></li><li><a href="#about">About</a></li><li><a href="#contact">Contact</a></li></ul></nav></header><main><section id="home"><h2>Home Section</h2><p>This is the home section content.</p></section></main></body></html>`,

    css: `.container{display:flex;justify-content:center;align-items:center;height:100vh;background:linear-gradient(45deg,#667eea,#764ba2);}.card{background:white;padding:2rem;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);max-width:400px;width:100%;}.title{font-size:2rem;color:#333;margin-bottom:1rem;text-align:center;}.button{background:#667eea;color:white;border:none;padding:0.75rem 1.5rem;border-radius:4px;cursor:pointer;transition:background 0.3s;}.button:hover{background:#5a67d8;}`,

    python: `def calculate_total(items, tax_rate=0.1):
    subtotal = sum(item['price'] * item['quantity'] for item in items)
    tax_amount = subtotal * tax_rate
    return {
        'subtotal': subtotal,
        'tax': tax_amount,
        'total': subtotal + tax_amount,
        'items': [{'name': item['name'], 'total': item['price'] * item['quantity']} for item in items]
    }

cart = [{'name': 'Laptop', 'price': 999.99, 'quantity': 1}, {'name': 'Mouse', 'price': 25.50, 'quantity': 2}]
result = calculate_total(cart, 0.08)
print(f"Order Summary: {result}")`,

    php: `<?php
function calculateTotal($items, $taxRate = 0.1) {
    $subtotal = array_reduce($items, function($sum, $item) {
        return $sum + ($item['price'] * $item['quantity']);
    }, 0);
    
    $taxAmount = $subtotal * $taxRate;
    
    return [
        'subtotal' => $subtotal,
        'tax' => $taxAmount,
        'total' => $subtotal + $taxAmount,
        'items' => array_map(function($item) {
            return array_merge($item, ['total' => $item['price'] * $item['quantity']]);
        }, $items)
    ];
}

$cart = [
    ['name' => 'Laptop', 'price' => 999.99, 'quantity' => 1],
    ['name' => 'Mouse', 'price' => 25.50, 'quantity' => 2]
];

$result = calculateTotal($cart, 0.08);
echo "Order Summary: " . json_encode($result);
?>`,

    dart: `class Product {
  final String name;
  final double price;
  final int quantity;
  
  Product({required this.name, required this.price, required this.quantity});
}

class OrderSummary {
  final double subtotal;
  final double tax;
  final double total;
  final List<Map<String, dynamic>> items;
  
  OrderSummary({required this.subtotal, required this.tax, required this.total, required this.items});
}

OrderSummary calculateTotal(List<Product> items, {double taxRate = 0.1}) {
  double subtotal = items.fold(0, (sum, item) => sum + (item.price * item.quantity));
  double taxAmount = subtotal * taxRate;
  
  return OrderSummary(
    subtotal: subtotal,
    tax: taxAmount,
    total: subtotal + taxAmount,
    items: items.map((item) => {'name': item.name, 'total': item.price * item.quantity}).toList(),
  );
}`,

    java: `public class OrderCalculator {
    public static class Product {
        public String name;
        public double price;
        public int quantity;
        
        public Product(String name, double price, int quantity) {
            this.name = name;
            this.price = price;
            this.quantity = quantity;
        }
    }
    
    public static OrderSummary calculateTotal(Product[] items, double taxRate) {
        double subtotal = 0;
        for (Product item : items) {
            subtotal += item.price * item.quantity;
        }
        
        double taxAmount = subtotal * taxRate;
        return new OrderSummary(subtotal, taxAmount, subtotal + taxAmount);
    }
}`,

    cpp: `#include <iostream>
#include <vector>
#include <numeric>

struct Product {
    std::string name;
    double price;
    int quantity;
};

struct OrderSummary {
    double subtotal;
    double tax;
    double total;
};

OrderSummary calculateTotal(const std::vector<Product>& items, double taxRate = 0.1) {
    double subtotal = std::accumulate(items.begin(), items.end(), 0.0,
        [](double sum, const Product& item) {
            return sum + (item.price * item.quantity);
        });
    
    double taxAmount = subtotal * taxRate;
    return {subtotal, taxAmount, subtotal + taxAmount};
}`,

    csharp: `using System;
using System.Linq;
using System.Collections.Generic;

public class Product {
    public string Name { get; set; }
    public decimal Price { get; set; }
    public int Quantity { get; set; }
}

public class OrderSummary {
    public decimal Subtotal { get; set; }
    public decimal Tax { get; set; }
    public decimal Total { get; set; }
}

public static OrderSummary CalculateTotal(List<Product> items, decimal taxRate = 0.1m) {
    var subtotal = items.Sum(item => item.Price * item.Quantity);
    var taxAmount = subtotal * taxRate;
    
    return new OrderSummary {
        Subtotal = subtotal,
        Tax = taxAmount,
        Total = subtotal + taxAmount
    };
}`,

    go: `package main

import "fmt"

type Product struct {
    Name     string
    Price    float64
    Quantity int
}

type OrderSummary struct {
    Subtotal float64
    Tax      float64
    Total    float64
}

func calculateTotal(items []Product, taxRate float64) OrderSummary {
    subtotal := 0.0
    for _, item := range items {
        subtotal += item.Price * float64(item.Quantity)
    }
    
    taxAmount := subtotal * taxRate
    return OrderSummary{
        Subtotal: subtotal,
        Tax:      taxAmount,
        Total:    subtotal + taxAmount,
    }
}`,

    rust: `struct Product {
    name: String,
    price: f64,
    quantity: u32,
}

struct OrderSummary {
    subtotal: f64,
    tax: f64,
    total: f64,
}

fn calculate_total(items: &[Product], tax_rate: f64) -> OrderSummary {
    let subtotal: f64 = items.iter()
        .map(|item| item.price * item.quantity as f64)
        .sum();
    
    let tax_amount = subtotal * tax_rate;
    
    OrderSummary {
        subtotal,
        tax: tax_amount,
        total: subtotal + tax_amount,
    }
}`,

    ruby: `class Product
  attr_accessor :name, :price, :quantity
  
  def initialize(name, price, quantity)
    @name = name
    @price = price
    @quantity = quantity
  end
end

class OrderSummary
  attr_accessor :subtotal, :tax, :total
  
  def initialize(subtotal, tax, total)
    @subtotal = subtotal
    @tax = tax
    @total = total
  end
end

def calculate_total(items, tax_rate = 0.1)
  subtotal = items.sum { |item| item.price * item.quantity }
  tax_amount = subtotal * tax_rate
  
  OrderSummary.new(subtotal, tax_amount, subtotal + tax_amount)
end`,

    swift: `struct Product {
    let name: String
    let price: Double
    let quantity: Int
}

struct OrderSummary {
    let subtotal: Double
    let tax: Double
    let total: Double
}

func calculateTotal(items: [Product], taxRate: Double = 0.1) -> OrderSummary {
    let subtotal = items.reduce(0) { sum, item in
        sum + (item.price * Double(item.quantity))
    }
    
    let taxAmount = subtotal * taxRate
    
    return OrderSummary(
        subtotal: subtotal,
        tax: taxAmount,
        total: subtotal + taxAmount
    )
}`,

    kotlin: `data class Product(
    val name: String,
    val price: Double,
    val quantity: Int
)

data class OrderSummary(
    val subtotal: Double,
    val tax: Double,
    val total: Double
)

fun calculateTotal(items: List<Product>, taxRate: Double = 0.1): OrderSummary {
    val subtotal = items.sumOf { it.price * it.quantity }
    val taxAmount = subtotal * taxRate
    
    return OrderSummary(
        subtotal = subtotal,
        tax = taxAmount,
        total = subtotal + taxAmount
    )
}`,

    xml: `<?xml version="1.0" encoding="UTF-8"?><catalog><book id="1"><title>The Great Gatsby</title><author>F. Scott Fitzgerald</author><price currency="USD">12.99</price><category>Fiction</category><availability>in-stock</availability></book><book id="2"><title>To Kill a Mockingbird</title><author>Harper Lee</author><price currency="USD">14.99</price><category>Classic</category><availability>in-stock</availability></book></catalog>`,

    yaml: `version: '3.8'
services:
  web:
    build: .
    ports:
      - "8000:8000"
    volumes:
      - .:/code
    environment:
      - DEBUG=1
      - DATABASE_URL=postgresql://user:pass@db:5432/myapp
    depends_on:
      - db
  db:
    image: postgres:13
    environment:
      POSTGRES_DB: myapp
      POSTGRES_USER: user
      POSTGRES_PASSWORD: pass
    volumes:
      - postgres_data:/var/lib/postgresql/data/
volumes:
  postgres_data:`,

    sql: `SELECT o.order_id, c.customer_name, c.email, o.order_date, SUM(oi.quantity * p.price) as total_amount FROM orders o JOIN customers c ON o.customer_id = c.customer_id JOIN order_items oi ON o.order_id = oi.order_id JOIN products p ON oi.product_id = p.product_id WHERE o.order_date >= DATE_SUB(CURDATE(), INTERVAL 30 DAY) GROUP BY o.order_id, c.customer_name, c.email, o.order_date HAVING total_amount > 100 ORDER BY total_amount DESC;`,

    markdown: `# Project Documentation

## Overview
This is a comprehensive guide to our **code formatting project**. It includes installation instructions, usage examples, and API documentation.

### Features
- ✅ Easy to use
- ✅ Highly customizable  
- ✅ Fast performance
- ✅ Cross-platform support

## Installation

\`\`\`bash
npm install code-formatter
\`\`\`

## Quick Start

\`\`\`javascript
const formatter = require('code-formatter');

const formattedCode = formatter.format(inputCode, {
  indentSize: 2,
  quotes: 'single'
});
console.log(formattedCode);
\`\`\`

> **Note:** Make sure you have Node.js version 14 or higher installed.`
  };

  const formatJavaScript = (code: string, options: FormattingOptions): string => {
    if (!code.trim()) return '';
    
    let formatted = code;
    
    // Remove console statements if requested
    if (options.removeConsole) {
      formatted = formatted.replace(/console\.(log|warn|error|info|debug)\([^)]*\);?/g, '');
    }
    
    // Convert to arrow functions if requested
    if (options.convertToArrowFunctions) {
      formatted = formatted.replace(/function\s+(\w+)\s*\((.*?)\)\s*{/g, 'const $1 = ($2) => {');
    }
    
    // Add strict mode if requested
    if (options.useStrictMode && !formatted.includes('"use strict"')) {
      formatted = '"use strict";\n\n' + formatted;
    }
    
    // Basic formatting improvements
    formatted = formatted
      .replace(/([=+\-*/%<>!&|])\s*([=+\-*/%<>!&|])/g, '$1$2')
      .replace(/([^=+\-*/%<>!&|])\s*([=+\-*/%<>!&|])\s*([^=+\-*/%<>!&|])/g, '$1 $2 $3')
      .replace(/,\s*/g, ', ')
      .replace(/:\s*/g, ': ')
      .replace(/;\s*/g, options.semicolons ? '; ' : ' ')
      .replace(/{\s*/g, options.bracketSpacing ? '{ ' : '{')
      .replace(/\s*}/g, options.bracketSpacing ? ' }' : '}')
      .replace(/\[\s*/g, options.bracketSpacing ? '[ ' : '[')
      .replace(/\s*\]/g, options.bracketSpacing ? ' ]' : ']')
      .replace(/\(\s*/g, '(')
      .replace(/\s*\)/g, ')')
      .replace(/=>\s*/g, ' => ')
      .replace(/function\s*\(/g, 'function (')
      .replace(/"/g, options.quotes === 'single' ? "'" : '"')
      .replace(/'([^'\\]*(\\.[^'\\]*)*)'(?=\s*[,;:\]})\s]|$)/g, options.quotes === 'double' ? '"$1"' : "'$1'");

    // Handle indentation
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indent = options.indentType === 'tabs' ? '\t' : ' '.repeat(options.indentSize);
    
    const formattedLines = lines.map((line: string) => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      
      if (trimmed.match(/^[\}\]\)]/)) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      const indented = indent.repeat(indentLevel) + trimmed;
      
      if (trimmed.match(/[\{\[\(]\s*$/)) {
        indentLevel++;
      }
      
      return indented;
    });

    formatted = formattedLines.join('\n');
    
    if (options.trailingCommas === 'none') {
      formatted = formatted.replace(/,(\s*[\}\]])/g, '$1');
    } else if (options.trailingCommas === 'all') {
      formatted = formatted.replace(/([^,\s])(\s*[\}\]])/g, '$1,$2');
    }
    
    if (options.insertFinalNewline && !formatted.endsWith('\n')) {
      formatted += '\n';
    }
    
    if (options.trimTrailingWhitespace) {
      formatted = formatted.replace(/[ \t]+$/gm, '');
    }
    
    return formatted;
  };

  const formatJSON = (jsonString: string, options: { indentSize: number }): string => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, options.indentSize);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      throw new Error('Invalid JSON: ' + errorMessage);
    }
  };

  const formatOtherLanguages = (code: string, lang: Language): string => {
    if (!code.trim()) return '';
    
    let formatted = code;
    const indent = '  '; // 2 spaces for all languages
    
    // Basic formatting for other languages
    switch (lang) {
      case 'html':
      case 'xml':
        formatted = code
          .replace(/>\s*</g, '>\n<')
          .replace(/(\w+)=/g, ' $1=')
          .replace(/"\s*>/g, '">')
          .replace(/(<\w+[^>]*>)([^<\n]+)(<\/\w+>)/g, '$1\n  $2\n$3');
        break;
        
      case 'css':
        formatted = code
          .replace(/{\s*/g, ' {\n  ')
          .replace(/;\s*/g, ';\n  ')
          .replace(/}\s*/g, '\n}\n')
          .replace(/,\s*/g, ',\n');
        break;
        
      case 'python':
        formatted = code
          .replace(/:\s*/g, ':\n    ')
          .replace(/,\s*/g, ', ')
          .replace(/=\s*/g, ' = ')
          .replace(/def\s+/g, 'def ');
        break;
        
      case 'sql':
        formatted = code
          .replace(/SELECT\s+/gi, 'SELECT ')
          .replace(/FROM\s+/gi, '\nFROM ')
          .replace(/WHERE\s+/gi, '\nWHERE ')
          .replace(/JOIN\s+/gi, '\nJOIN ')
          .replace(/GROUP BY\s+/gi, '\nGROUP BY ')
          .replace(/ORDER BY\s+/gi, '\nORDER BY ');
        break;
        
      default:
        // Generic formatting for other languages
        formatted = code
          .replace(/{\s*/g, ' {\n  ')
          .replace(/}\s*/g, '\n}\n')
          .replace(/;\s*/g, ';\n')
          .replace(/,\s*/g, ', ');
    }
    
    // Handle basic indentation
    const lines = formatted.split('\n');
    let indentLevel = 0;
    
    const formattedLines = lines.map((line: string) => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      
      if (trimmed.match(/^[\}\]\)]/)) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      const indented = indent.repeat(indentLevel) + trimmed;
      
      if (trimmed.match(/[\{\[\(]\s*$/) || trimmed.endsWith(':')) {
        indentLevel++;
      }
      
      return indented;
    });

    return formattedLines.filter(line => line.trim()).join('\n');
  };

  const formatCode = async () => {
    if (!inputCode.trim()) {
      setError('Please enter some code to format');
      return;
    }

    setIsFormatting(true);
    setError('');

    try {
      let result;
      
      if (language === 'json') {
        result = formatJSON(inputCode, { indentSize });
      } else if (language === 'javascript' || language === 'typescript') {
        result = formatJavaScript(inputCode, {
          indentSize,
          indentType,
          semicolons,
          quotes,
          trailingCommas,
          bracketSpacing,
          arrowParens,
          maxLineLength,
          insertFinalNewline,
          trimTrailingWhitespace,
          preserveComments,
          sortImports,
          removeConsole,
          convertToArrowFunctions,
          addTypeAnnotations,
          useStrictMode
        });
      } else {
        result = formatOtherLanguages(inputCode, language);
      }
      
      setOutputCode(result);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Error formatting code: ${errorMessage}`);
    } finally {
      setIsFormatting(false);
    }
  };

  const minifyCode = () => {
    if (!inputCode.trim()) {
      setError('Please enter some code to minify');
      return;
    }

    try {
      let minified = inputCode
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\/\/.*$/gm, '')
        .replace(/\s+/g, ' ')
        .replace(/\s*([{}();,:])\s*/g, '$1')
        .replace(/\s*([=+\-*/%<>!&|])\s*/g, '$1')
        .trim();

      setOutputCode(minified);
      setError('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Error minifying code: ${errorMessage}`);
    }
  };

  useEffect(() => {
    if (autoFormat && inputCode && inputCode.length > 10) {
      const timer = setTimeout(formatCode, 500);
      return () => clearTimeout(timer);
    }
  }, [inputCode, language, indentSize, indentType, semicolons, quotes, autoFormat]);

  useEffect(() => {
    if (!inputCode) {
      setInputCode(sampleCode[language] || '// Sample code not available for this language');
    }
  }, [language]);

  const stats = useMemo(() => {
    const inputLines = inputCode.split('\n').length;
    const outputLines = outputCode.split('\n').length;
    const inputChars = inputCode.length;
    const outputChars = outputCode.length;
    const compressionRatio = inputChars > 0 ? ((outputChars - inputChars) / inputChars * 100) : 0;
    
    return {
      inputLines,
      inputChars,
      inputWords: inputCode.split(/\s+/).filter(word => word.length > 0).length,
      outputLines,
      outputChars,
      outputWords: outputCode.split(/\s+/).filter(word => word.length > 0).length,
      compression: compressionRatio.toFixed(1)
    };
  }, [inputCode, outputCode]);

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const downloadFile = (content: string, filename: string): void => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputCode(result);
        }
      };
      reader.readAsText(file);
    }
  };

  const loadSample = () => {
    setInputCode(sampleCode[language] || '// Sample code not available for this language');
    setOutputCode('');
    setError('');
  };

  const clearAll = () => {
    setInputCode('');
    setOutputCode('');
    setError('');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const isDark = darkMode;
  const themeClasses = {
    bg: isDark ? 'bg-gray-900' : 'bg-gray-50',
    card: isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-gray-300' : 'text-gray-600',
    input: isDark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    button: isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900',
    buttonPrimary: 'bg-blue-500 hover:bg-blue-600 text-white',
    buttonSuccess: 'bg-green-500 hover:bg-green-600 text-white',
    buttonWarning: 'bg-orange-500 hover:bg-orange-600 text-white',
    modal: isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300',
    overlay: 'bg-black bg-opacity-50'
  };

  const selectedLanguage = languageOptions.find(lang => lang.value === language);

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'} p-4 ${themeClasses.bg} ${themeClasses.text}`}>
      {/* Compact Header */}
      <div className={`mb-6 p-4 rounded-lg ${themeClasses.card} border shadow-sm`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <Code className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Code Formatter Pro</h1>
              <p className={`text-sm ${themeClasses.textSecondary}`}>Professional Multi-Language Code Formatting Tool</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${themeClasses.button}`}
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg transition-colors ${themeClasses.button}`}
              title="Toggle Dark Mode"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setShowSettingsPanel(true)}
              className={`p-2 rounded-lg transition-colors ${themeClasses.button}`}
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Statistics Bar */}
        {showStats && (
          <div className="grid grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} className={`p-2 text-center rounded ${themeClasses.card} border`}>
                <div className="text-lg font-bold text-blue-400">{value}{key === 'compression' ? '%' : ''}</div>
                <div className={`text-xs ${themeClasses.textSecondary} capitalize`}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Control Panel */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as Language)}
              className={`pl-8 pr-3 py-2 rounded border ${themeClasses.input} focus:ring-2 focus:ring-blue-500 text-sm appearance-none min-w-[140px]`}
            >
              {languageOptions.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-sm">
              {selectedLanguage?.icon}
            </span>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={formatCode}
              disabled={isFormatting}
              className={`px-4 py-2 rounded transition-colors flex items-center space-x-2 ${themeClasses.buttonPrimary} disabled:opacity-50 text-sm`}
            >
              {isFormatting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              <span>{isFormatting ? 'Formatting...' : 'Format'}</span>
            </button>
            <button
              onClick={minifyCode}
              className={`px-4 py-2 rounded transition-colors flex items-center space-x-2 ${themeClasses.buttonWarning} text-sm`}
            >
              <Square className="w-4 h-4" />
              <span>Minify</span>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <span className={`text-sm ${themeClasses.textSecondary}`}>Indent:</span>
            <input
              type="number"
              min="1"
              max="8"
              value={indentSize}
              onChange={(e) => setIndentSize(Number(e.target.value))}
              className={`w-12 px-2 py-1 rounded border ${themeClasses.input} focus:ring-2 focus:ring-blue-500 text-center text-sm`}
            />
            <select
              value={indentType}
              onChange={(e) => setIndentType(e.target.value as 'spaces' | 'tabs')}
              className={`px-2 py-1 rounded border ${themeClasses.input} focus:ring-2 focus:ring-blue-500 text-sm`}
            >
              <option value="spaces">Spaces</option>
              <option value="tabs">Tabs</option>
            </select>
          </div>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoFormat}
              onChange={(e) => setAutoFormat(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className={`text-sm ${themeClasses.textSecondary}`}>Auto Format</span>
          </label>

          <button
            onClick={loadSample}
            className={`px-3 py-2 rounded transition-colors ${themeClasses.button} text-sm`}
          >
            Load Sample
          </button>

          <button
            onClick={clearAll}
            className={`px-3 py-2 rounded transition-colors ${themeClasses.button} text-sm`}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded flex items-center space-x-2 text-red-400">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Main Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Input Panel */}
        <div className={`rounded-lg ${themeClasses.card} border overflow-hidden`}>
          <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-semibold flex items-center space-x-2">
              <FileText className="w-4 h-4 text-blue-400" />
              <span>Input ({selectedLanguage?.label})</span>
            </h3>
            <div className="flex space-x-1">
              <button
                onClick={() => copyToClipboard(inputCode)}
                className={`p-1.5 rounded transition-colors ${themeClasses.button}`}
                title="Copy Input"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                onClick={() => setInputCode('')}
                className={`p-1.5 rounded transition-colors ${themeClasses.button}`}
                title="Clear Input"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="relative">
            <textarea
              ref={inputRef}
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
              className={`w-full ${isFullscreen ? 'h-[calc(100vh-300px)]' : 'h-80'} p-3 font-mono resize-none focus:outline-none bg-transparent ${
                wordWrap ? 'whitespace-pre-wrap' : 'whitespace-pre overflow-x-auto'
              } ${showLineNumbers ? 'pl-12' : ''} ${themeClasses.input.split(' ')[2]}`}
              style={{ fontSize: `${fontSize}px`, lineHeight: '1.4' }}
              placeholder={`Enter your ${selectedLanguage?.label} code here...`}
            />
            {showLineNumbers && (
              <div className={`absolute left-0 top-0 w-10 p-3 text-xs ${themeClasses.textSecondary} pointer-events-none font-mono border-r ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {inputCode.split('\n').map((_, i) => (
                  <div key={i} style={{ lineHeight: '1.4', fontSize: `${fontSize}px` }}>{i + 1}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Output Panel */}
        <div className={`rounded-lg ${themeClasses.card} border overflow-hidden`}>
          <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-semibold flex items-center space-x-2">
              <Code className="w-4 h-4 text-green-400" />
              <span>Formatted Code</span>
            </h3>
            <div className="flex space-x-1">
              <button
                onClick={() => copyToClipboard(outputCode)}
                className={`p-1.5 rounded transition-colors ${copied ? 'bg-green-500 text-white' : themeClasses.button}`}
                title="Copy Output"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={() => downloadFile(outputCode, `formatted.${language === 'typescript' ? 'ts' : language === 'json' ? 'json' : language}`)}
                className={`p-1.5 rounded transition-colors ${themeClasses.button}`}
                title="Download Output"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="relative">
            <pre
              ref={outputRef}
              className={`w-full ${isFullscreen ? 'h-[calc(100vh-300px)]' : 'h-80'} p-3 font-mono overflow-auto ${
                wordWrap ? 'whitespace-pre-wrap' : 'whitespace-pre'
              } ${themeClasses.textSecondary} ${showLineNumbers ? 'pl-12' : ''} bg-transparent`}
              style={{ fontSize: `${fontSize}px`, lineHeight: '1.4' }}
            >
              {outputCode || (
                <span className="text-gray-500 italic">Formatted code will appear here...</span>
              )}
            </pre>
            {showLineNumbers && outputCode && (
              <div className={`absolute left-0 top-0 w-10 p-3 text-xs ${themeClasses.textSecondary} pointer-events-none font-mono border-r ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {outputCode.split('\n').map((_, i) => (
                  <div key={i} style={{ lineHeight: '1.4', fontSize: `${fontSize}px` }}>{i + 1}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsPanel && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${themeClasses.overlay}`}>
          <div className={`max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-lg ${themeClasses.modal} border shadow-2xl`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-gray-600' : 'border-gray-200'} sticky top-0 ${themeClasses.modal}`}>
              <h3 className="text-xl font-bold flex items-center space-x-2">
                <Settings className="w-5 h-5 text-orange-400" />
                <span>Advanced Settings</span>
              </h3>
              <button
                onClick={() => setShowSettingsPanel(false)}
                className={`p-2 rounded-lg transition-colors ${themeClasses.button}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {/* Display Settings */}
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold text-blue-400 flex items-center space-x-2">
                    <Palette className="w-5 h-5" />
                    <span>Display Options</span>
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Font Size</label>
                      <input
                        type="range"
                        min="10"
                        max="24"
                        value={fontSize}
                        onChange={(e) => setFontSize(Number(e.target.value))}
                        className="w-full accent-blue-500"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>10px</span>
                        <span className="font-medium">{fontSize}px</span>
                        <span>24px</span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {[
                        { key: 'wordWrap', label: 'Word Wrap', state: wordWrap, setState: setWordWrap },
                        { key: 'showLineNumbers', label: 'Line Numbers', state: showLineNumbers, setState: setShowLineNumbers },
                        { key: 'showStats', label: 'Show Statistics', state: showStats, setState: setShowStats },
                        { key: 'autoFormat', label: 'Auto Format', state: autoFormat, setState: setAutoFormat }
                      ].map(option => (
                        <div key={option.key} className="flex items-center justify-between">
                          <label className="text-sm font-medium">{option.label}</label>
                          <button
                            onClick={() => option.setState(!option.state)}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              option.state ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                          >
                            <div className={`absolute w-5 h-5 bg-white rounded-full transition-transform ${
                              option.state ? 'translate-x-6' : 'translate-x-0.5'
                            } top-0.5`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Code Formatting Settings */}
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold text-green-400 flex items-center space-x-2">
                    <Code className="w-5 h-5" />
                    <span>Code Formatting</span>
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Quote Style</label>
                      <select
                        value={quotes}
                        onChange={(e) => setQuotes(e.target.value as 'single' | 'double')}
                        className={`w-full p-2 rounded border ${themeClasses.input} focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="single">Single Quotes (')</option>
                        <option value="double">Double Quotes (")</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Trailing Commas</label>
                      <select
                        value={trailingCommas}
                        onChange={(e) => setTrailingCommas(e.target.value as 'none' | 'es5' | 'all')}
                        className={`w-full p-2 rounded border ${themeClasses.input} focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="none">None</option>
                        <option value="es5">ES5 Compatible</option>
                        <option value="all">All Possible</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Arrow Function Parentheses</label>
                      <select
                        value={arrowParens}
                        onChange={(e) => setArrowParens(e.target.value as 'avoid' | 'always')}
                        className={`w-full p-2 rounded border ${themeClasses.input} focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="avoid">Avoid when possible</option>
                        <option value="always">Always include</option>
                      </select>
                    </div>

                    <div className="space-y-3">
                      {[
                        { key: 'semicolons', label: 'Semicolons', state: semicolons, setState: setSemicolons },
                        { key: 'bracketSpacing', label: 'Bracket Spacing', state: bracketSpacing, setState: setBracketSpacing },
                        { key: 'insertFinalNewline', label: 'Final Newline', state: insertFinalNewline, setState: setInsertFinalNewline },
                        { key: 'trimTrailingWhitespace', label: 'Trim Whitespace', state: trimTrailingWhitespace, setState: setTrimTrailingWhitespace }
                      ].map(option => (
                        <div key={option.key} className="flex items-center justify-between">
                          <label className="text-sm font-medium">{option.label}</label>
                          <button
                            onClick={() => option.setState(!option.state)}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              option.state ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                          >
                            <div className={`absolute w-5 h-5 bg-white rounded-full transition-transform ${
                              option.state ? 'translate-x-6' : 'translate-x-0.5'
                            } top-0.5`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Advanced Options */}
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold text-purple-400 flex items-center space-x-2">
                    <Terminal className="w-5 h-5" />
                    <span>Advanced Options</span>
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Line Length</label>
                      <input
                        type="range"
                        min="40"
                        max="200"
                        value={maxLineLength}
                        onChange={(e) => setMaxLineLength(Number(e.target.value))}
                        className="w-full accent-purple-500"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>40</span>
                        <span className="font-medium">{maxLineLength}</span>
                        <span>200</span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {[
                        { key: 'preserveComments', label: 'Preserve Comments', state: preserveComments, setState: setPreserveComments },
                        { key: 'sortImports', label: 'Sort Imports', state: sortImports, setState: setSortImports },
                        { key: 'removeConsole', label: 'Remove Console Logs', state: removeConsole, setState: setRemoveConsole },
                        { key: 'convertToArrowFunctions', label: 'Convert to Arrow Functions', state: convertToArrowFunctions, setState: setConvertToArrowFunctions },
                        { key: 'addTypeAnnotations', label: 'Add Type Annotations', state: addTypeAnnotations, setState: setAddTypeAnnotations },
                        { key: 'useStrictMode', label: 'Add "use strict"', state: useStrictMode, setState: setUseStrictMode }
                      ].map(option => (
                        <div key={option.key} className="flex items-center justify-between">
                          <label className="text-sm font-medium">{option.label}</label>
                          <button
                            onClick={() => option.setState(!option.state)}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              option.state ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                          >
                            <div className={`absolute w-5 h-5 bg-white rounded-full transition-transform ${
                              option.state ? 'translate-x-6' : 'translate-x-0.5'
                            } top-0.5`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Preset Configurations */}
              <div className="mt-8 pt-6 border-t border-gray-600">
                <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center space-x-2">
                  <Filter className="w-5 h-5" />
                  <span>Quick Presets</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { name: 'Standard', config: { indentSize: 2, quotes: 'single', semicolons: true, trailingCommas: 'es5' } },
                    { name: 'Google Style', config: { indentSize: 2, quotes: 'single', semicolons: true, trailingCommas: 'all' } },
                    { name: 'Airbnb Style', config: { indentSize: 2, quotes: 'single', semicolons: true, trailingCommas: 'all' } },
                    { name: 'Prettier', config: { indentSize: 2, quotes: 'double', semicolons: true, trailingCommas: 'es5' } }
                  ].map(preset => (
                    <button
                      key={preset.name}
                      onClick={() => {
                        setIndentSize(preset.config.indentSize);
                        setQuotes(preset.config.quotes as 'single' | 'double');
                        setSemicolons(preset.config.semicolons);
                        setTrailingCommas(preset.config.trailingCommas as 'none' | 'es5' | 'all');
                      }}
                      className={`p-3 rounded border ${themeClasses.button} text-sm font-medium transition-colors`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Panel for JSON */}
      {showPreview && language === 'json' && outputCode && (
        <div className={`mt-4 rounded-lg ${themeClasses.card} border`}>
          <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-semibold flex items-center space-x-2">
              <Eye className="w-4 h-4 text-purple-400" />
              <span>JSON Tree View</span>
            </h3>
            <button
              onClick={() => setShowPreview(false)}
              className={`p-1.5 rounded transition-colors ${themeClasses.button}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            <div className={`p-3 rounded border ${themeClasses.input} max-h-64 overflow-auto font-mono text-sm`}>
              {JSON.stringify(JSON.parse(outputCode || '{}'), null, 2)}
            </div>
          </div>
        </div>
      )}

      {/* Quick Action Buttons */}
      <div className="fixed bottom-4 right-4 flex flex-col space-y-2">
        {language === 'json' && (
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`p-3 rounded-full ${themeClasses.buttonPrimary} shadow-lg hover:shadow-xl transition-all`}
            title="Toggle JSON Preview"
          >
            {showPreview ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
        
        <button
          onClick={() => {
            const element = document.getElementById('file-upload') as HTMLInputElement;
            element?.click();
          }}
          className={`p-3 rounded-full ${themeClasses.buttonSuccess} shadow-lg hover:shadow-xl transition-all`}
          title="Upload File"
        >
          <Upload className="w-5 h-5" />
        </button>
        
        <button
          className={`p-3 rounded-full ${themeClasses.button} shadow-lg hover:shadow-xl transition-all`}
          title="Help & Shortcuts"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Hidden File Input */}
      <input
        id="file-upload"
        type="file"
        accept=".js,.ts,.json,.html,.css,.py,.php,.dart,.java,.cpp,.cs,.go,.rs,.rb,.swift,.kt,.xml,.yml,.yaml,.sql,.md,.txt"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Footer */}
      <footer className={`mt-8 py-4 text-center border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          Code Formatter Pro - Professional Multi-Language Code Formatting Tool
        </p>
        <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
          Supporting 20+ Programming Languages • Built with React & Tailwind CSS
        </p>
      </footer>
    </div>
  );
};

export default JavaScriptFormatterPro;